<template>
  <el-dialog
      v-el-drag-dialog
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      title="下载明细"
      width="760px"
      top="10vh"
      append-to-body>
    <el-form label-width="90px" style="padding-right: 20px;">
      <el-form-item label="金额类型">
        <el-select v-model="amountType" placeholder="请选择金额类型" style="width: 100%">
          <el-option v-for="item in amountTypeOptions" :key="item.value" :value="item.value" :label="item.label"/>
        </el-select>
      </el-form-item>
      <el-form-item label="组织范围">
        <div class="org-tree-tip">勾选组织后，将导出所选组织（含其下全部区域）的对应金额类型明细。</div>
        <el-tree
            ref="orgTree"
            :data="orgTree"
            show-checkbox
            node-key="id"
            :props="treeProps"
            :default-expand-all="true"
            :expand-on-click-node="false"
            class="org-tree">
          <span slot-scope="{ data }" class="custom-tree-node">
            <span>{{data.label}}</span>
          </span>
        </el-tree>
      </el-form-item>
    </el-form>
    <div slot="footer">
      <el-button size="mini" @click="dialogVisible=false">取消</el-button>
      <el-button type="primary" size="mini" :loading="exportLoading" @click="handleExport">导出</el-button>
    </div>
  </el-dialog>
</template>

<script>
  import * as XLSX from "xlsx";

  // 金额类型 -> 汇总行上对应的明细子表字段
  const DETAIL_FIELD_MAP = {
    "已签收": "signedAmountDetails",
    "已发运未签收": "shippedUnsignedAmountDetails",
    "已排产未发运": "producedUnshippedAmountDetails",
    "已排产已提交": "producedSubmittedAmountDetails",
    "已下单未排产": "orderedUnproducedAmountDetails",
    "已下单": "monthOrderedAmountDetails",
  };

  // 单个导出工作簿上限行数（前端本地生成 Excel，行数过大时阻塞浏览器，需缩小范围）
  const MAX_EXPORT_ROWS = 100000;

  export default {
    name: "SalesRevenueDetailDownload",
    data() {
      return {
        dialogVisible: false,
        exportLoading: false,
        amountType: "",
        amountTypeOptions: [
          {value: "已签收", label: "已签收"},
          {value: "已发运未签收", label: "已发运未签收"},
          {value: "已排产未发运", label: "已排产未发运"},
          {value: "已排产已提交", label: "已排产已提交"},
          {value: "已下单未排产", label: "已下单未排产"},
          {value: "已下单", label: "已下单"},
        ],
        treeProps: {
          children: "children",
          label: "label",
        },
        orgTree: [],
        rows: [],
        filter: {},
      };
    },
    methods: {
      open(orgTree, rows, filter) {
        this.orgTree = orgTree || [];
        this.rows = rows || [];
        this.filter = filter || {};
        this.amountType = "";
        this.dialogVisible = true;
        this.$nextTick(() => {
          if (this.$refs.orgTree) {
            this.$refs.orgTree.setCheckedKeys([]);
          }
        });
      },
      // 递归收集某（组）节点子树下所有区域 departmentId
      collectDeptIds(node, set) {
        if (node.departmentId) {
          set.add(String(node.departmentId));
          return;
        }
        if (Array.isArray(node.children)) {
          node.children.forEach(child => this.collectDeptIds(child, set));
        }
      },
      collectCheckedDeptIds() {
        const set = new Set();
        const checked = this.$refs.orgTree.getCheckedNodes();
        (checked || []).forEach(node => this.collectDeptIds(node, set));
        return set;
      },
      handleExport() {
        if (!this.amountType) {
          return this.$message.warning("请先选择金额类型.");
        }
        const deptIds = this.collectCheckedDeptIds();
        if (deptIds.size === 0) {
          return this.$message.warning("请至少勾选一个组织.");
        }
        const field = DETAIL_FIELD_MAP[this.amountType];
        // 从汇总行中筛出属于所选组织范围、且该金额类型下有明细的区域行。
        // 海外行以 departmentId 匹配；国内行（departmentId 为 null、组织名为 SAP销售组）以 orgName 匹配
        const rows = (this.rows || []).filter(row => {
          if (Array.isArray(row[field]) === false || row[field].length === 0) {
            return false;
          }
          if (row.departmentId != null && String(row.departmentId) !== "") {
            return deptIds.has(String(row.departmentId));
          }
          return deptIds.has(String(row.orgName || ""));
        });

        const header = ["出厂编号", "订单号", "发货单号", "销售员", "客户", "下单日期", "交期", "交期+物流周期", "签收日期", "发运日期", "发运日期+物流周期", "计划调整交期", "计划调整交期+物流周期", "物流周期(天)", "金额(万元)", "区域"];
        const body = [];
        rows.forEach(row => {
          (row[field] || []).forEach(d => {
            body.push([
              d.factoryNo || "",
              d.orderNumber || "",
              d.invoiceNo || "",
              d.employeeName || "",
              d.customerName || "",
              d.makingDate || "",
              d.invoiceTime || "",
              d.invoiceTimeCalc || "",
              d.signDate || "",
              d.shipDate || "",
              d.shipDateCalc || "",
              d.adjustDeliveryDate || "",
              d.adjustDeliveryDateCalc || "",
              (d.cycleDays === null || d.cycleDays === undefined) ? "" : d.cycleDays,
              this.toWan(d.amount),
              d.departmentName || "",
            ]);
          });
        });

        if (body.length === 0) {
          return this.$message.warning("所选组织在该金额类型下没有可导出的明细.");
        }
        if (body.length > MAX_EXPORT_ROWS) {
          this.$confirm(`所选明细共 ${body.length} 行，超过浏览器可稳定生成的 ${MAX_EXPORT_ROWS} 行，建议收窄组织范围后分批导出。仍要继续吗?`, "提示", {type: "warning"})
            .then(() => this.doExport(header, body))
            .catch(() => {});
          return;
        }
        this.doExport(header, body);
      },
      doExport(header, body) {
        this.exportLoading = true;
        // 让 loading 先渲染，再在下一帧执行阻塞的 Excel 组装
        this.$nextTick(() => {
          try {
            const aoa = [header].concat(body);
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            // 简单设置首行加粗表头（可选，不影响核心功能）
            ws["!cols"] = [
              {wch: 16}, {wch: 16}, {wch: 16}, {wch: 10}, {wch: 18},
              {wch: 12}, {wch: 12}, {wch: 14}, {wch: 12}, {wch: 12},
              {wch: 16}, {wch: 14}, {wch: 18}, {wch: 10}, {wch: 12},
              {wch: 14},
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, this.amountType.slice(0, 31));
            const out = XLSX.write(wb, {bookType: "xlsx", type: "array"});
            const fileName = this.buildFileName();
            const blob = new Blob([out], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
              window.navigator.msSaveBlob(blob, fileName);
            } else {
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = fileName;
              a.style.display = "none";
              document.body.appendChild(a);
              a.click();
              URL.revokeObjectURL(a.href);
              document.body.removeChild(a);
            }
            this.exportLoading = false;
            this.$message.success("导出成功.");
          } catch (e) {
            this.exportLoading = false;
            this.$message.error("导出失败：" + (e && e.message ? e.message : e));
          }
        });
      },
      buildFileName() {
        const beginTime = this.filter.beginTime || "";
        const reportType = this.filter.reportType === 1 ? "年度累计" : "月度";
        return `销售收入明细_${this.amountType}_${beginTime}(${reportType}).xlsx`;
      },
      toWan(amount) {
        if (amount === null || amount === undefined || amount === "") {
          return "";
        }
        const val = Number(amount);
        if (Number.isNaN(val)) {
          return "";
        }
        return Math.round((val / 10000) * 100) / 100;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .org-tree-tip {
    color: #909399;
    font-size: 12px;
    line-height: 18px;
    margin-bottom: 6px;
  }
  .org-tree {
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    max-height: 46vh;
    overflow: auto;
    padding: 4px;
  }
</style>
